import { ArrowRight, CalendarCheck, Heart, Menu, Wallet, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../Logo";
import { Modal } from "../Modal";
import { BackButton, ThemeToggle } from "./AppShell";
import { PageHeaderProvider, usePageHeaderValue } from "./PageHeaderContext";
import { WEBSITE_URL } from "../../lib/website";
import { authPath } from "../../lib/return-path";

/**
 * Guests browse the trip catalogue (Explora and the trip pages) without an account. Anything that
 * belongs to a customer (reserving, saving, an Apartado, credit) asks them to sign up first and
 * brings them back to the same page afterwards.
 */
interface GuestContextValue {
  guest: boolean;
  /**
   * Opens the "create your account" prompt; `reason` completes "Crea tu cuenta para …". `returnTo`
   * replaces the current page as the place to come back to (e.g. with `?intent=` to reopen an action).
   */
  askForAccount(reason: string, returnTo?: string): void;
}

const GuestContext = createContext<GuestContextValue>({ guest: false, askForAccount: () => undefined });

export function useGuest(): GuestContextValue {
  return useContext(GuestContext);
}

export function GuestShell() {
  return (
    <PageHeaderProvider>
      <GuestPromptProvider>
        <GuestFrame />
      </GuestPromptProvider>
    </PageHeaderProvider>
  );
}

const websiteMenu = [
  { href: `${WEBSITE_URL}/destinos`, label: "Destinos" },
  { href: `${WEBSITE_URL}/nosotros`, label: "Nosotros" },
  { href: `${WEBSITE_URL}/contacto`, label: "Contacto" }
];

function useReturnPath() {
  const location = useLocation();
  return `${location.pathname}${location.search}${location.hash}`;
}

function GuestFrame() {
  const header = usePageHeaderValue();
  const { pathname } = useLocation();
  const from = useReturnPath();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); setMenuOpen(false); }, [pathname]);

  return (
    <div className="app-shell guest-shell">
      <header className="guest-bar">
        <a href={WEBSITE_URL} className="guest-brand" aria-label="Aventuria · inicio"><Logo height={24} /></a>
        <nav className={`guest-menu${menuOpen ? " open" : ""}`} aria-label="Principal" id="guest-menu">
          <NavLink to="/explorar" className={({ isActive }) => `guest-menu-link${isActive || pathname.startsWith("/viajes/") ? " active" : ""}`}>Viajes</NavLink>
          {websiteMenu.map((item) => <a key={item.href} href={item.href} className="guest-menu-link">{item.label}</a>)}
          <Link to={authPath("/login", from)} state={{ from }} className="guest-menu-link guest-menu-login">Iniciar sesión</Link>
        </nav>
        <div className="guest-bar-actions">
          <ThemeToggle />
          <Link className="secondary-button small guest-login" to={authPath("/login", from)} state={{ from }}>Iniciar sesión</Link>
          <Link className="primary-button solid small" to={authPath("/registro", from)} state={{ from }}>Crear cuenta</Link>
          <button type="button" className="topbar-button guest-menu-toggle" onClick={() => setMenuOpen((open) => !open)} aria-expanded={menuOpen} aria-controls="guest-menu" aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}>
            {menuOpen ? <X size={18} aria-hidden="true" /> : <Menu size={18} aria-hidden="true" />}
          </button>
        </div>
      </header>
      <div className="app-content">
        <header className="topbar">
          {pathname !== "/explorar" && <BackButton home="/explorar" />}
          <div className="topbar-copy">
            <h1>{header.title}</h1>
            {header.subtitle && <span className="topbar-subtitle">{header.subtitle}</span>}
          </div>
          {header.actions && <div className="topbar-actions">{header.actions}</div>}
        </header>
        <main className="app-main"><Outlet /></main>
      </div>
    </div>
  );
}

function GuestPromptProvider({ children }: { children: ReactNode }) {
  const [prompt, setPrompt] = useState<{ reason: string; returnTo?: string } | null>(null);
  const askForAccount = useCallback((reason: string, returnTo?: string) => setPrompt({ reason, returnTo }), []);
  const value = useMemo(() => ({ guest: true, askForAccount }), [askForAccount]);
  return (
    <GuestContext.Provider value={value}>
      {children}
      <AccountPrompt reason={prompt?.reason ?? null} returnTo={prompt?.returnTo} onClose={() => setPrompt(null)} />
    </GuestContext.Provider>
  );
}

function AccountPrompt({ reason, returnTo, onClose }: { reason: string | null; returnTo?: string; onClose(): void }) {
  const navigate = useNavigate();
  const current = useReturnPath();
  const from = returnTo ?? current;
  const go = (path: "/registro" | "/login") => { onClose(); navigate(authPath(path, from), { state: { from } }); };
  return (
    <Modal open={reason !== null} onOpenChange={(open) => { if (!open) onClose(); }} label="Crea tu cuenta">
      <div className="dialog-content guest-prompt">
        <button type="button" className="dialog-close" onClick={onClose} aria-label="Cerrar"><X size={20} aria-hidden="true" /></button>
        <span className="page-eyebrow">Tu cuenta Aventuria</span>
        <h2>Crea tu cuenta para {reason ?? "continuar"}</h2>
        <p>Es gratis y toma un minuto. Al terminar volverás a este viaje.</p>
        <ul className="guest-prompt-list">
          <li><CalendarCheck size={16} aria-hidden="true" /> Solicita tu reserva y recibe la propuesta de un asesor</li>
          <li><Wallet size={16} aria-hidden="true" /> Aparta tu salida desde tu Wallet Libre</li>
          <li><Heart size={16} aria-hidden="true" /> Guarda viajes y recibe avisos cuando bajen de precio</li>
        </ul>
        <div className="guest-prompt-actions">
          <button type="button" className="primary-button solid full" onClick={() => go("/registro")}>Crear cuenta <ArrowRight size={15} aria-hidden="true" /></button>
          <button type="button" className="secondary-button full" onClick={() => go("/login")}>Ya tengo cuenta</button>
        </div>
      </div>
    </Modal>
  );
}
